import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import { AccountDetails, MAX_PHOTO_BYTES, Offer, OfferInput, venueImageUrl } from '../../api';

// Handed from PartnerLayout to every portal page through <Outlet context>.
export interface PortalContext {
  account: AccountDetails | null;
  // Adds the admin console's ?restoId= to a portal path, so links keep acting on the same venue.
  to: (path: string) => string;
  // Re-fetch the top bar's claim count after something changes it.
  refreshDashboard: () => void;
}

export const usePortal = () => useOutletContext<PortalContext>();

export type Notice = { severity: 'success' | 'error' | 'info'; text: string } | null;

export function NoticeBox({ notice }: { notice: Notice }) {
  return notice ? <div className={`pp-notice ${notice.severity}`} role={notice.severity === 'error' ? 'alert' : 'status'}>{notice.text}</div> : null;
}

export const errorMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

export const ACCEPTED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// Client-side pre-check so the partner gets instant feedback; the API re-validates the bytes.
export const photoProblem = (file: File): string | null => {
  if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) return `${file.name}: only JPG, PNG or WebP images are supported.`;
  if (file.size > MAX_PHOTO_BYTES) return `${file.name}: images must be 5 MB or smaller.`;
  return null;
};

const formatDate = (iso?: string | null, withYear = false) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}) }) : '';

export const formatTime = (time?: string | null) => {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  return new Date(2000, 0, 1, h, m).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

export const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

// 1234 -> "1.2K", as on the proposal's offer cards.
export const compact = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, '')}K` : String(n));

// "25 Dec 2024 - 31 Dec 2024"
export function dateRange(offer: Pick<Offer, 'startDate' | 'endDate'>): string {
  if (!offer.startDate && !offer.endDate) return 'Dates not set';
  return `${formatDate(offer.startDate, true) || '…'} - ${formatDate(offer.endDate, true) || '…'}`;
}

// Customer-facing line for a live offer: "Valid until 30 Sep · 8:00 PM – 2:00 AM".
export function publicValidity(offer: { endDate?: string | null; startTime?: string | null; endTime?: string | null }): string {
  const hours = offer.startTime && offer.endTime
    ? `${formatTime(offer.startTime)} – ${formatTime(offer.endTime)}`
    : offer.endTime ? `Until ${formatTime(offer.endTime)}` : '';
  return [offer.endDate ? `Valid until ${formatDate(offer.endDate)}` : '', hours].filter(Boolean).join(' · ') || 'Valid now';
}

// Everything the API needs to save an offer again, e.g. to pause it from the list.
export const offerToInput = (o: Offer): OfferInput => ({
  title: o.title, offerType: o.offerType, benefit: o.benefit, description: o.description,
  startDate: o.startDate, endDate: o.endDate, startTime: o.startTime, endTime: o.endTime,
  maxClaims: o.maxClaims, claimsPerUser: o.claimsPerUser, terms: o.terms, minimumSpend: o.minimumSpend,
  isPublished: o.isPublished, isActive: o.isActive, isFeatured: o.isFeatured,
  category: o.category, coversAlcohol: o.coversAlcohol, discountType: o.discountType, discountValue: o.discountValue,
  // Re-sending an unchanged offer (e.g. pause/resume) keeps the partner's existing confirmation.
  activeDays: o.activeDays ?? [], confirmAccuracy: false
});

export function StatusBadge({ status }: { status: string }) {
  return <span className={`pp-badge ${status.toLowerCase()}`}>{status}</span>;
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (checked: boolean) => void; label: string }) {
  return (
    <label className="pp-toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <i aria-hidden="true" />
      {label}
    </label>
  );
}

// The horizontal offer card from the proposal: cover image, badges, title and benefit, venue,
// date range, claims and views. `actions` sits top-right (Edit / View / more menu).
export function OfferRow({ offer, venueName, selected, actions }: { offer: Offer; venueName?: string; selected?: boolean; actions?: React.ReactNode }) {
  const cover = venueImageUrl(offer.coverImageUrl);
  return (
    <article className={`pp-offer ${selected ? 'selected' : ''}`}>
      <div className="pp-offer-img">{cover && <img src={cover} alt="" loading="lazy" />}</div>
      <div className="pp-offer-body">
        <div className="pp-offer-top">
          <div className="pp-badges">
            {offer.isFeatured && offer.status === 'Active' && !offer.hiddenReason
              ? <StatusBadge status="Featured" />
              : <StatusBadge status={offer.status === 'NeedsCategory' ? 'Needs Category' : offer.status === 'Pending' ? 'In Review' : offer.status} />}
          </div>
          {actions && <div className="pp-offer-actions">{actions}</div>}
        </div>
        <h4 title={offer.title}>{offer.title}</h4>
        {offer.benefit && <div className="pp-offer-sub">{offer.benefit}</div>}
        <div className="pp-offer-meta">
          {venueName && <span><LocationOnIcon className="pin" />{venueName}</span>}
          <span><CalendarMonthOutlinedIcon />{dateRange(offer)}</span>
        </div>
        <div className="pp-offer-meta">
          <span><BarChartIcon className="bars" />{compact(offer.claimsCount)} Claims&nbsp;&nbsp;|&nbsp;&nbsp;{compact(offer.views)} Views</span>
        </div>
        {offer.status === 'NeedsCategory' && (
          <div className="pp-notice info" style={{ margin: '8px 0 0', padding: '6px 9px', fontSize: 12 }}>Hidden from customers until you choose a category. Edit and update it.</div>
        )}
        {offer.status === 'Rejected' && (
          <div className="pp-notice error" style={{ margin: '8px 0 0', padding: '6px 9px', fontSize: 12 }}>
            Not approved{offer.moderationNote ? `: ${offer.moderationNote}` : ''}. Edit and update it to send it for review again.
          </div>
        )}
        {offer.hiddenReason && <div className="pp-notice info" style={{ margin: '8px 0 0', padding: '6px 9px', fontSize: 12 }}>{offer.hiddenReason}</div>}
        {offer.status === 'Suspended' && (
          <div className="pp-notice error" style={{ margin: '8px 0 0', padding: '6px 9px', fontSize: 12 }}>
            Suspended by Cheers2Night{offer.moderationNote ? `: ${offer.moderationNote}` : ''}. Contact support to have it reinstated.
          </div>
        )}
      </div>
    </article>
  );
}

export function EditLink({ to }: { to: string }) {
  return <Link className="pp-btn pp-outline pp-sm" to={to}><EditOutlinedIcon />Edit</Link>;
}

export function PageHead({ title, subtitle, children }: { title: string; subtitle?: string; children?: React.ReactNode }) {
  return (
    <div className="pp-head">
      <div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
      {children}
    </div>
  );
}

export function ComingSoon({ title }: { title: string }) {
  const { to } = usePortal();
  return (
    <>
      <PageHead title={title} subtitle="This section is coming soon." />
      <div className="pp-panel pp-empty">
        <h3>Not available yet</h3>
        <p className="pp-muted">We're still building {title.toLowerCase()} for partners. Your offers, gallery and claims are ready to use now.</p>
        <Link className="pp-btn pp-gold" to={to('/partner-account/offers')}>Go to My Offers</Link>
      </div>
    </>
  );
}
